// src/utils/cloche.ts — SCE Issues
// La cloche de l'appli (03/11, maquette https://claude.ai/artifact/AG5cv9AuMJZ68Yy5UgWW9M) :
// ce que montre chaque notification, et quand elle s'efface. Pur, testable hors ligne.
//
// ─── D'où viennent les lignes ───
//
// Liste SCE_Notifications (OP_SCE) : une ligne par notification ET par
// destinataire. Les flows 1, 4 et 7 écrivent les leurs (docs/flows/
// cloche-dans-les-flows.md) ; l'appli écrit celles qu'elle compose (mentions,
// FNC-T, envois au BE) en même temps que la ligne de SCE_Envois.
//
// ─── Éphémères ───
//
// Pas de colonne « lu » : ouvrir une notification l'efface, la croix l'efface
// sans l'ouvrir, « Tout effacer » vide la cloche. Ouvrir la fiche par un AUTRE
// chemin (liste, mail, recherche) efface aussi celles qui y mènent : on a vu.
// Plus de 30 jours : effacée à la lecture suivante. La pastille compte ce qui reste.

/** mention · suivi (flow 4) · nouveau (flow 1) · fnct · be · rappel (flow 7). */
export type GenreNotification = 'mention' | 'suivi' | 'nouveau' | 'fnct' | 'be' | 'rappel'

const GENRES: readonly GenreNotification[] = ['mention', 'suivi', 'nouveau', 'fnct', 'be', 'rappel']

export const NOM_LISTE_NOTIFICATIONS = 'SCE_Notifications'

/** Au-delà, une notification est effacée à la lecture. */
export const DUREE_VIE_JOURS = 30

/** Longueur de l'extrait (les flows coupent au même endroit). */
export const LONGUEUR_EXTRAIT = 300

/** Une ligne de SCE_Notifications, lue. */
export interface NotificationAppli {
  /** Identifiant de la ligne SharePoint. */
  id: string
  /** Création de la ligne, ISO. */
  le: string
  /** `P2026-0187`, `FNC-T_2026-58`, « vos actions FNC-T ». */
  reference: string
  genre: GenreNotification
  auteur: string
  auteurUpn: string
  /** « a commenté », « vous a mentionné dans »… */
  quoi: string
  /** Le titre du problème, la description de la FNC-T. */
  libelle: string
  extrait: string
  /** « Vous suivez ce problème » ; vide = elle vous est adressée. */
  raison: string
  /** Chemin dans l'appli (`/issues/P2026-0187`). */
  lien: string
}

/** Une notification à écrire (colonnes de la liste). */
export interface NouvelleNotification {
  destinataire: string
  reference: string
  genre: GenreNotification
  auteur: string
  auteurUpn: string
  quoi: string
  libelle: string
  extrait?: string
  raison?: string
  lien: string
}

const texte = (v: unknown): string => (typeof v === 'string' ? v.trim() : '')

/** Coupe à `LONGUEUR_EXTRAIT` caractères, « … » au bout. */
export function couperExtrait(t: string): string {
  const net = t.trim()
  return net.length > LONGUEUR_EXTRAIT ? `${net.slice(0, LONGUEUR_EXTRAIT).trimEnd()}…` : net
}

/**
 * Le chemin dans l'appli, quoi que le flow ait écrit : `/issues/P2026-0187`,
 * l'adresse complète du site, ou sans `/` de tête. Vide si illisible.
 */
export function cheminDuLien(lien: string): string {
  const brut = lien.trim()
  if (!brut) return ''
  if (/^https?:\/\//i.test(brut)) {
    try {
      return new URL(brut).pathname
    } catch {
      return ''
    }
  }
  return brut.startsWith('/') ? brut : `/${brut}`
}

/** Une ligne (champs SharePoint) → notification ; null sans destinataire ni référence. */
export function lireNotification(id: string, le: string, champs: Record<string, unknown>): NotificationAppli | null {
  const reference = texte(champs.Title)
  if (!reference) return null
  const genreBrut = texte(champs.Genre).toLowerCase()
  const genre = (GENRES as readonly string[]).includes(genreBrut) ? (genreBrut as GenreNotification) : 'suivi'
  return {
    id,
    le,
    reference,
    genre,
    auteur: texte(champs.Auteur),
    auteurUpn: texte(champs.AuteurUPN).toLowerCase(),
    quoi: texte(champs.Quoi),
    libelle: texte(champs.Libelle),
    extrait: couperExtrait(texte(champs.Extrait)),
    raison: texte(champs.Raison),
    lien: cheminDuLien(texte(champs.Lien)),
  }
}

/** Les colonnes à écrire ; destinataire en minuscules (c'est sur lui qu'on filtre). */
export function champsNotificationAppli(n: NouvelleNotification): Record<string, string> {
  return {
    Title: n.reference.trim(),
    Destinataire: n.destinataire.trim().toLowerCase(),
    Genre: n.genre,
    Auteur: n.auteur.trim(),
    AuteurUPN: n.auteurUpn.trim().toLowerCase(),
    Quoi: n.quoi.trim(),
    Libelle: n.libelle.trim().slice(0, 255),
    Extrait: couperExtrait(n.extrait ?? ''),
    Raison: (n.raison ?? '').trim(),
    Lien: cheminDuLien(n.lien),
  }
}

/** Les plus récentes d'abord. */
export function trierNotifications(liste: readonly NotificationAppli[]): NotificationAppli[] {
  return [...liste].sort((a, b) => b.le.localeCompare(a.le))
}

/** Plus de `DUREE_VIE_JOURS` jours : à effacer. */
export function estPerimee(n: Pick<NotificationAppli, 'le'>, maintenant: Date): boolean {
  const t = Date.parse(n.le)
  return Number.isFinite(t) && maintenant.getTime() - t > DUREE_VIE_JOURS * 86_400_000
}

const memeJour = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

const deuxChiffres = (n: number): string => String(n).padStart(2, '0')

/** « à l'instant », « il y a 5 min », « il y a 2 h », « hier, 16:12 », « 28/10, 16:12 ». */
export function quandNotification(le: string, maintenant: Date): string {
  const d = new Date(le)
  if (!Number.isFinite(d.getTime())) return ''
  const minutes = Math.floor((maintenant.getTime() - d.getTime()) / 60_000)
  const heure = `${deuxChiffres(d.getHours())}:${deuxChiffres(d.getMinutes())}`
  if (minutes < 1) return 'à l’instant'
  if (minutes < 60) return `il y a ${minutes} min`
  if (memeJour(d, maintenant)) return `il y a ${Math.floor(minutes / 60)} h`
  const hier = new Date(maintenant)
  hier.setDate(hier.getDate() - 1)
  if (memeJour(d, hier)) return `hier, ${heure}`
  return `${deuxChiffres(d.getDate())}/${deuxChiffres(d.getMonth() + 1)}, ${heure}`
}

/** « il y a 5 min · Vous suivez ce problème ». */
export function piedNotification(n: Pick<NotificationAppli, 'le' | 'raison'>, maintenant: Date): string {
  const quand = quandNotification(n.le, maintenant)
  return n.raison ? `${quand} · ${n.raison}` : quand
}

/** Aujourd'hui, puis plus tôt (déjà triées). */
export function parJour(
  liste: readonly NotificationAppli[],
  maintenant: Date,
): { aujourdhui: NotificationAppli[]; avant: NotificationAppli[] } {
  const aujourdhui: NotificationAppli[] = []
  const avant: NotificationAppli[] = []
  for (const n of liste) (memeJour(new Date(n.le), maintenant) ? aujourdhui : avant).push(n)
  return { aujourdhui, avant }
}

/** L'icône du médaillon. */
export type IconeNotification = 'mention' | 'declaration' | 'clos' | 'annule' | 'fnct' | 'be' | 'qualification' | 'commentaire'

export function iconeNotification(n: Pick<NotificationAppli, 'genre' | 'quoi'>): IconeNotification {
  const quoi = n.quoi.toLowerCase()
  if (n.genre === 'mention') return 'mention'
  if (n.genre === 'fnct' || n.genre === 'rappel') return 'fnct'
  if (n.genre === 'be' || quoi.includes('envoyé au be')) return 'be'
  if (n.genre === 'nouveau' || quoi.includes('déclaré')) return 'declaration'
  // Le DERNIER geste l'emporte : « a qualifié, commenté et clôturé » finit clos.
  const gestes: Array<[string, IconeNotification]> = [
    ['clôtur', 'clos'],
    ['annul', 'annule'],
    ['comment', 'commentaire'],
    ['qualifi', 'qualification'],
  ]
  let meilleur: IconeNotification = 'commentaire'
  let position = -1
  for (const [mot, icone] of gestes) {
    const i = quoi.lastIndexOf(mot)
    if (i > position) {
      position = i
      meilleur = icone
    }
  }
  return meilleur
}

/** Chemin comparable : sans paramètres, décodé, sans `/` final, en minuscules. */
function cleChemin(chemin: string): string {
  let c = chemin.replace(/[?#].*$/, '')
  try {
    c = decodeURIComponent(c)
  } catch {
    // Laissé tel quel.
  }
  return c.replace(/\/+$/, '').toLowerCase()
}

/** Les notifications qui mènent à la page ouverte : vues, donc à effacer. */
export function notificationsDeLaPage(liste: readonly NotificationAppli[], chemin: string): NotificationAppli[] {
  const ici = cleChemin(chemin)
  if (!ici || ici === '/' || ici === '') return []
  return liste.filter((n) => n.lien && cleChemin(n.lien) === ici)
}
